import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Spin,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
  EditOutlined,
  UserOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminApi } from '@/services/api';
import { formatMoney } from '@utils/formatMoney';
import { apiErrorMessage } from '@utils/apiError';
import { canRecheckGoogle, describeGoogleRecheck, googleCheckSummary } from '@utils/googleRecheck';
import {
  formatIstRefundTime,
  formatStorePrice,
  summarizeRevokedItems,
  type RevokedItems,
} from '@utils/storePrice';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_COLORS: Record<string, string> = {
  completed: 'green',
  pending: 'orange',
  failed: 'red',
  refunded: 'purple',
  cancelled: 'default',
};

interface PurchaseDetail {
  id: string;
  userId: string;
  orderId?: string;
  purchaseToken?: string;
  productId: string;
  purchaseType: string;
  platform: string;
  status: string;
  amount: number;
  currency: string;
  refundedAt?: string | null;
  storePriceMicros?: string | null;
  storeCurrency?: string | null;
  storeRegion?: string | null;
  revokedItems?: RevokedItems | null;
  rawResponse?: Record<string, unknown>;
  subscriptionExpiryDate?: string;
  isAutoRenewing?: boolean;
  isAcknowledged?: boolean;
  userName?: string;
  userEmail?: string;
  productName?: string;
  createdAt: string;
  updatedAt: string;
}

/** An admin refund made with "keep items" records `refundInfo.revokeItems === false`. */
function keptByAdmin(raw: Record<string, unknown> | undefined): boolean {
  const info = raw?.refundInfo;
  return (
    typeof info === 'object' &&
    info !== null &&
    (info as { revokeItems?: unknown }).revokeItems === false
  );
}

export const PurchaseShow: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState<PurchaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [statusForm] = Form.useForm();
  const [refundForm] = Form.useForm();

  const fetchPurchase = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getPurchaseDetail(id!);
      setPurchase(res.data);
    } catch {
      message.error('Failed to load purchase details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPurchase();
  }, [fetchPurchase]);

  const handleUpdateStatus = async (values: { status: string; reason?: string }) => {
    try {
      await adminApi.updatePurchaseStatus(id!, values);
      message.success('Status updated');
      setStatusModalVisible(false);
      statusForm.resetFields();
      fetchPurchase();
    } catch {
      message.error('Failed to update status');
    }
  };

  const handleRecheckGoogle = () => {
    Modal.confirm({
      title: 'Check this purchase again with Google?',
      content:
        'If Google now says purchased, the player gets the product and the purchase is completed. ' +
        "Otherwise it stays failed with Google's latest answer.",
      okText: 'Check again',
      onOk: async () => {
        try {
          const res = await adminApi.recheckGooglePurchase(id!);
          const result = res.data;
          const { ok, text } = describeGoogleRecheck(result);
          if (ok) {
            message.success(text);
          } else {
            message.warning(text);
          }
          // The detail is read from the replica, which can lag behind this
          // check: show its answer now and read the row again shortly.
          setPurchase((p) =>
            p ? { ...p, status: result.status, orderId: result.orderId ?? p.orderId } : p
          );
          setTimeout(fetchPurchase, 2000);
        } catch (err) {
          message.error(apiErrorMessage(err, 'Failed to check the purchase with Google'));
          fetchPurchase();
        }
      },
    });
  };

  const handleRefund = async (values: { reason: string; revokeItems: boolean }) => {
    try {
      await adminApi.refundPurchase(id!, values);
      message.success('Purchase refunded');
      setRefundModalVisible(false);
      refundForm.resetFields();
      fetchPurchase();
    } catch {
      message.error('Failed to refund purchase');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!purchase) {
    return <Card>Purchase not found</Card>;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/purchases')}>
            Back
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            Purchase Details
          </Title>
        </Space>
        <Space>
          {canRecheckGoogle(purchase) && (
            <Button type="primary" icon={<ReloadOutlined />} onClick={handleRecheckGoogle}>
              Check again with Google
            </Button>
          )}
          {purchase.status !== 'refunded' && (
            <>
              <Button icon={<EditOutlined />} onClick={() => setStatusModalVisible(true)}>
                Update Status
              </Button>
              <Button
                danger
                icon={<RollbackOutlined />}
                onClick={() => setRefundModalVisible(true)}
              >
                Refund
              </Button>
            </>
          )}
        </Space>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Purchase Details */}
        <Col xs={24} lg={14}>
          <Card title="Transaction Details" size="small">
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Purchase ID">
                <Tooltip title={purchase.id}>
                  <Text copyable={{ text: purchase.id }} style={{ fontSize: 12 }}>
                    {purchase.id.slice(0, 12)}...
                  </Text>
                </Tooltip>
              </Descriptions.Item>
              <Descriptions.Item label="Order ID">
                {purchase.orderId ? (
                  <Text copyable={{ text: purchase.orderId }} style={{ fontSize: 12 }}>
                    {purchase.orderId}
                  </Text>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLORS[purchase.status] || 'default'}>
                  {purchase.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              {purchase.status === 'failed' && googleCheckSummary(purchase.rawResponse) && (
                <Descriptions.Item label="Google check" span={2}>
                  {googleCheckSummary(purchase.rawResponse)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Amount">
                {(() => {
                  const cur = (purchase.currency || '').toUpperCase();
                  const amt = Number(purchase.amount || 0);
                  const COLORS: Record<string, string> = {
                    INR: '#3f8600',
                    USD: '#3f8600',
                    EUR: '#3f8600',
                    GBP: '#3f8600',
                    COINS: '#faad14',
                    MATCHTOKENS: '#722ed1',
                    EVOTOKENS: '#13c2c2',
                    GEMS: '#eb2f96',
                    PREMIUMCURRENCY: '#eb2f96',
                  };
                  return (
                    <Text strong style={{ fontSize: 16, color: COLORS[cur] }}>
                      {formatMoney(amt, cur)}
                    </Text>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Store price">
                {purchase.storePriceMicros && purchase.storeCurrency
                  ? `${formatStorePrice(purchase.storePriceMicros, purchase.storeCurrency)}${
                      purchase.storeRegion ? ` · ${purchase.storeRegion}` : ''
                    }`
                  : '—'}
              </Descriptions.Item>
              {purchase.status === 'refunded' && (
                <>
                  <Descriptions.Item label="Refunded at">
                    {formatIstRefundTime(purchase.refundedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Clawback">
                    {summarizeRevokedItems(purchase.revokedItems, purchase.status, {
                      keptByAdmin: keptByAdmin(purchase.rawResponse),
                    })}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Platform">
                <Tag>{(purchase.platform || '').replace('_', ' ').toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag>{(purchase.purchaseType || '').toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Product">
                {purchase.productName || purchase.productId}
              </Descriptions.Item>
              <Descriptions.Item label="Acknowledged">
                <Tag color={purchase.isAcknowledged ? 'green' : 'orange'}>
                  {purchase.isAcknowledged ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {dayjs(purchase.createdAt).format('DD MMM YYYY, hh:mm A')}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {dayjs(purchase.updatedAt).format('DD MMM YYYY, hh:mm A')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* User Info + Subscription */}
        <Col xs={24} lg={10}>
          <Card
            title="User"
            size="small"
            extra={
              <Button
                type="link"
                icon={<UserOutlined />}
                onClick={() => navigate(`/players/${purchase.userId}`)}
              >
                View Player
              </Button>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="User ID">
                <Text copyable={{ text: purchase.userId }} style={{ fontSize: 12 }}>
                  {purchase.userId}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Name">{purchase.userName || '-'}</Descriptions.Item>
            </Descriptions>
          </Card>

          {purchase.purchaseType === 'subscription' && (
            <Card title="Subscription" size="small" style={{ marginTop: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Expiry">
                  {purchase.subscriptionExpiryDate
                    ? dayjs(purchase.subscriptionExpiryDate).format('DD MMM YYYY, hh:mm A')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Auto-Renewing">
                  <Tag color={purchase.isAutoRenewing ? 'green' : 'default'}>
                    {purchase.isAutoRenewing ? 'Yes' : 'No'}
                  </Tag>
                </Descriptions.Item>
                {purchase.subscriptionExpiryDate && (
                  <Descriptions.Item label="Days Remaining">
                    <Tag
                      color={
                        dayjs(purchase.subscriptionExpiryDate).diff(dayjs(), 'day') <= 7
                          ? 'red'
                          : 'green'
                      }
                    >
                      {dayjs(purchase.subscriptionExpiryDate).diff(dayjs(), 'day')}d
                    </Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}
        </Col>
      </Row>

      {/* Verification Data */}
      {purchase.rawResponse && Object.keys(purchase.rawResponse).length > 0 && (
        <Card title="Verification Data" size="small" style={{ marginTop: 16 }}>
          <pre
            style={{
              background: '#fafafa',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              maxHeight: 300,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
            }}
          >
            {JSON.stringify(purchase.rawResponse, null, 2)}
          </pre>
        </Card>
      )}

      {/* Update Status Modal */}
      <Modal
        title="Update Purchase Status"
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleUpdateStatus}>
          <Form.Item name="status" label="New Status" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <TextArea rows={3} placeholder="Optional reason for status change" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Update Status
          </Button>
        </Form>
      </Modal>

      {/* Refund Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            Refund Purchase
          </Space>
        }
        open={refundModalVisible}
        onCancel={() => setRefundModalVisible(false)}
        footer={null}
      >
        <Form
          form={refundForm}
          layout="vertical"
          onFinish={handleRefund}
          initialValues={{ revokeItems: true }}
        >
          <Form.Item
            name="reason"
            label="Refund Reason"
            rules={[{ required: true, message: 'Enter refund reason' }]}
          >
            <TextArea rows={3} placeholder="Why is this purchase being refunded?" />
          </Form.Item>
          <Form.Item name="revokeItems" label="Revoke Granted Items" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            This will mark the purchase as refunded. If revoke items is enabled, granted
            currencies/items will be removed from the user.
          </Text>
          <Button type="primary" danger htmlType="submit" block>
            Confirm Refund
          </Button>
        </Form>
      </Modal>
    </div>
  );
};
